// @ts-nocheck
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FormActions, { FormActionsProps } from "@store/Library/Components/Toolkit/FormActions";
import ArrowForward from "@store/Library/Components/Toolkit/Icons/ArrowForward";
import InfoFill from "@store/Library/Components/Toolkit/Icons/Info/InfoFill";
import { formGridProps } from "@store/Themes/common/props/form";
import useResetPasswordForm from "@store/Framework/Common/Hooks/useResetPasswordForm";
import ResetPasswordJobTitleField, {
    ResetPasswordJobTitleFieldPresentationProps,
} from "@store/Library/Components/ResetPassword/ResetPasswordJobTitleField";
import PasswordField, { PasswordFieldPresentationProps } from "@store/Library/Components/PasswordField";
import ResetPasswordUserNameField from "@store/Library/Components/ResetPassword/ResetPasswordUserNameField";
import PasswordRequirements, {
    PasswordRequirementsPresentationProps,
} from "@store/Library/Components/Toolkit/PasswordRequirements";
import Tooltip, { TooltipPresentationProps } from "@store/MobiusOverrides/Tooltip/Tooltip";
import constants from "@store/constants";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { useSession } from "@toolkit/Framework/Store/Context/ContextHooks";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";
import SignedOutSplitLayout, {
    SignedOutSplitLayoutPresentationProps,
} from "@store/Library/Components/SignedOut/SignedOutSplitLayout";
import AccountApplicationSideContent, {
    AccountApplicationSideContentPresentationProps,
} from "@store/Library/Components/AccountApplication/AccountApplicationSideContent";

const enum fields {
    logo = "logo",
    title = "title",
    content = "content",
    showContactUs = "showContactUs",
    sideContentIcon = "sideContentIcon",
    sideContentTitle = "sideContentTitle",
    sideContentBody = "sideContentBody",
}

export interface AccountApplicationRegistrationProps extends WidgetProps {
    fields: {
        [fields.logo]: string;
        [fields.title]: string;
        [fields.content]: string;
        [fields.showContactUs]: boolean;
        [fields.sideContentIcon]: string;
        [fields.sideContentTitle]: string;
        [fields.sideContentBody]: string;
    };
}

export interface AccountApplicationRegistrationStyles {
    accountApplicationSideContent?: AccountApplicationSideContentPresentationProps;
    authorizedRepresentativeField?: TextFieldPresentationProps;
    authorizedRepresentativeGridItem?: GridItemProps;
    confirmPasswordField?: PasswordFieldPresentationProps;
    confirmPasswordGridItem?: GridItemProps;
    contentWrapper?: InjectableCss;
    formActions?: FormActionsProps;
    formGridContainer?: GridContainerProps;
    headingWrapper?: InjectableCss;
    jobTitleField?: ResetPasswordJobTitleFieldPresentationProps;
    loadingOverlay?: LoadingOverlayProps;
    passwordField?: PasswordFieldPresentationProps;
    passwordGridItem?: GridItemProps;
    passwordRequirements?: PasswordRequirementsPresentationProps;
    requirementsHintText?: TypographyPresentationProps;
    requirementsHintTooltip?: TooltipPresentationProps;
    requirementsHintWrapper?: InjectableCss;
    signedOutSplitLayout?: SignedOutSplitLayoutPresentationProps;
    submitButton?: ButtonPresentationProps;
    title?: TypographyPresentationProps;
    userNameField?: TextFieldPresentationProps;
    userNameGridItem?: GridItemProps;
}

export const accountApplicationRegistrationStyles: AccountApplicationRegistrationStyles = {
    loadingOverlay: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: ${pxToRem(32)};
        `,
    },
    headingWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: ${pxToRem(12)};
        `,
    },
    title: {
        variant: "h1",
        css: css`
            margin: 0;
        `,
    },
    formGridContainer: formGridProps.gridContainer,
    authorizedRepresentativeGridItem: { width: 12 },
    userNameGridItem: { width: 12 },
    passwordGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    confirmPasswordGridItem: { width: 12 },
    jobTitleField: {
        useOverlay: true,
        extendedStyles: {
            gridItem: { width: 12 },
            otherGridItem: { width: 12 },
        },
    },
    requirementsHintWrapper: {
        css: css`
            display: flex;
            align-items: center;
            margin-top: ${pxToRem(8)};
            font-size: ${x => x.theme.design.typography.size.smaller};
        `,
    },
    requirementsHintTooltip: {
        iconProps: { src: InfoFill, size: 20 },
        mergeCss: true,
        cssOverrides: {
            tooltipBody: css`
                text-align: left;
            `,
        },
    },
    passwordRequirements: {
        showTitle: false,
        overridedStyles: {
            wrapper: null as any,
        },
    },
    submitButton: {
        icon: {
            src: ArrowForward,
            position: "right",
        },
    },
    formActions: { spacing: "0" },
};

const styles = accountApplicationRegistrationStyles;

const AccountApplicationRegistration: FC<AccountApplicationRegistrationProps> = ({
    fields: { logo, content, showContactUs, sideContentBody, sideContentIcon, sideContentTitle, title },
}) => {
    const {
        changeConfirmPassword,
        changeJobTitles,
        changeOtherJobTitle,
        changePassword,
        confirmPassword,
        confirmPasswordErrorMessage,
        isSubmitting,
        jobTitleErrorMessage,
        jobTitles,
        otherJobTitle,
        otherJobTitleErrorMessage,
        password,
        passwordErrorMessage,
        submit,
    } = useResetPasswordForm({ mode: "activate" });

    const authorizedRepresentative = useSession()?.properties?.greifAuthorizedRepresentativeFullName ?? "";

    return (
        <SignedOutSplitLayout
            {...styles.signedOutSplitLayout}
            data-test-selector="accountApplicationRegistration"
            headerProps={{ logo, showContactUs, isProspectUser: true }}
            sideContent={
                <AccountApplicationSideContent
                    {...styles.accountApplicationSideContent}
                    body={sideContentBody}
                    title={sideContentTitle}
                    titleImageSrc={sideContentIcon}
                />
            }
        >
            <LoadingOverlay {...styles.loadingOverlay} loading={isSubmitting}>
                {(!!title || !!content) && (
                    <StyledWrapper {...styles.headingWrapper}>
                        {!!title && <Typography {...styles.title}>{title}</Typography>}
                        {!!content && (
                            <StyledWrapper {...styles.contentWrapper}>{parse(content, parserOptions)}</StyledWrapper>
                        )}
                    </StyledWrapper>
                )}
                <GridContainer {...styles.formGridContainer}>
                    {!!authorizedRepresentative && (
                        <GridItem {...styles.authorizedRepresentativeGridItem}>
                            <TextField
                                {...styles.authorizedRepresentativeField}
                                className="disabled"
                                label={translate("Authorized Representative")}
                                readOnly
                                type="text"
                                value={authorizedRepresentative}
                            />
                        </GridItem>
                    )}
                    <GridItem {...styles.userNameGridItem}>
                        <ResetPasswordUserNameField {...styles.userNameField} />
                    </GridItem>
                    <GridItem {...styles.passwordGridItem}>
                        <PasswordField
                            {...styles.passwordField}
                            clickableText={translate("Show Password")}
                            data-test-selector="resetPassword_NewPassword"
                            error={passwordErrorMessage}
                            id="password"
                            label={translate("Password")}
                            onChange={event => changePassword(event.currentTarget.value)}
                            required
                            value={password}
                        />
                        <StyledWrapper {...styles.requirementsHintWrapper}>
                            <Typography {...styles.requirementsHintText}>
                                {translate("Password Requirements")}
                            </Typography>
                            <Tooltip
                                {...styles.requirementsHintTooltip}
                                text={<PasswordRequirements {...styles.passwordRequirements} />}
                                triggerAltText={translate("Password Requirements")}
                            />
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...styles.confirmPasswordGridItem}>
                        <PasswordField
                            {...styles.confirmPasswordField}
                            clickableText={translate("Show Confirm Password")}
                            data-test-selector="resetPassword_ConfirmNewPassword"
                            error={confirmPasswordErrorMessage}
                            id="confirm-password"
                            label={translate("Confirm Password")}
                            onChange={event => changeConfirmPassword(event.currentTarget.value)}
                            required
                            value={confirmPassword}
                        />
                    </GridItem>
                    <ResetPasswordJobTitleField
                        {...styles.jobTitleField}
                        error={jobTitleErrorMessage}
                        onChange={changeJobTitles}
                        onOtherChange={changeOtherJobTitle}
                        otherError={otherJobTitleErrorMessage}
                        otherValue={otherJobTitle}
                        value={jobTitles}
                    />
                </GridContainer>
                <FormActions {...styles.formActions}>
                    <Button {...styles.submitButton} data-test-selector="resetPassword_ResetButton" onClick={submit}>
                        {translate("Get Started")}
                    </Button>
                </FormActions>
            </LoadingOverlay>
        </SignedOutSplitLayout>
    );
};

const widgetModule: WidgetModule = {
    component: AccountApplicationRegistration,
    definition: {
        displayName: "Account Application Registration",
        group: constants.widgetGroup.accountApplication,
        allowedContexts: [constants.pageType.resetPassword],
        fieldDefinitions: [
            {
                name: fields.logo,
                displayName: "Logo",
                editorTemplate: "ImagePickerField",
                defaultValue: constants.logoPath,
                fieldType: "Contextual",
            },
            {
                name: fields.showContactUs,
                displayName: "Show Contact Us",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Start Your Account Application",
                fieldType: "Contextual",
            },
            {
                name: fields.content,
                displayName: "Content",
                editorTemplate: "RichTextField",
                defaultValue: "<p>Create a password to activate your Greif+ account.</p>",
                fieldType: "Contextual",
            },
            {
                name: fields.sideContentIcon,
                displayName: "Side Content Icon",
                editorTemplate: "ImagePickerField",
                defaultValue: "/userfiles/account-application/registration-title-icon.svg",
                fieldType: "Contextual",
            },
            {
                name: fields.sideContentTitle,
                displayName: "Side Content Title",
                editorTemplate: "TextField",
                defaultValue: "Before you begin",
                fieldType: "Contextual",
            },
            {
                name: fields.sideContentBody,
                displayName: "Side Content",
                editorTemplate: "RichTextField",
                fieldType: "Contextual",
                defaultValue: `
                    <p>Before you begin, make sure you have all documents below — you'll need them to complete the Account Application.</p>
                    <ul>
                        <li>Your company's tax ID,</li>
                        <li>Tax exemption certificates,</li>
                        <li>Bank Reference,</li>
                        <li>Trade references.</li>
                    </ul>
                `,
            },
        ],
    },
};

export default widgetModule;
